import { useLocation, matchPath } from 'react-router-dom'
import { SeoHead } from './SeoHead'

// Per-route SEO. Keyed by the React Router path pattern, not the literal
// pathname, so dynamic segments (e.g. /account/org/invite/:token) resolve
// to a single entry.
//
// React 19 hoists <title>, <meta>, and <link> from this component into
// <head>. As the route changes, useLocation() re-runs and SeoHead emits
// the new tags. The static <title>/<meta> in index.html is what scanners
// see on the very first request to / before JS executes; once React
// mounts, this component takes over.

type RouteMeta = {
  title: string
  description: string
  noindex?: boolean
}

const SITE_TAGLINE = 'GeoBorder'

const ROUTE_META: Array<{ pattern: string; meta: RouteMeta }> = [
  {
    pattern: '/',
    meta: {
      title: `GeoBorder · Jurisdiction intelligence for any coordinate`,
      description:
        'Click any point on a worldwide map to reverse-geocode it into its full stack of U.S. and global jurisdictions — country, state, county, congressional district — with boundary-distance context.',
    },
  },
  {
    pattern: '/api',
    meta: {
      title: `Developer API · ${SITE_TAGLINE}`,
      description:
        'Public reverse-geocoding API. Resolve any lat/lon to its full jurisdictional stack with stable IDs, source provenance, and edge-distance context. Free tier available.',
    },
  },
  {
    pattern: '/docs',
    meta: {
      title: `API Reference · ${SITE_TAGLINE}`,
      description:
        'Interactive OpenAPI 3.1 reference for the GeoBorder developer API. Try every endpoint live with your API key.',
    },
  },
  {
    pattern: '/explore',
    meta: {
      title: `Map Explorer · ${SITE_TAGLINE}`,
      description:
        'Interactive map for exploring jurisdictional boundaries. Hover, measure, toggle layers, and inspect the same data the GeoBorder API returns.',
    },
  },
  {
    pattern: '/data',
    meta: {
      title: `Data sources · ${SITE_TAGLINE}`,
      description:
        'Every boundary GeoBorder returns is sourced from an authoritative, publicly-available dataset — U.S. Census TIGER/Line, Cartographic Boundary files, the Administrative Office of the U.S. Courts, and Natural Earth. Full attribution, vintage, license, and source links per dataset.',
    },
  },
  {
    pattern: '/sign-in',
    meta: {
      title: `Sign In · ${SITE_TAGLINE}`,
      description:
        'Sign in to your GeoBorder account to manage API keys, view usage, and administer your organization.',
    },
  },
  {
    pattern: '/sign-up',
    meta: {
      title: `Sign Up · ${SITE_TAGLINE}`,
      description:
        'Create a free GeoBorder account to generate API keys, view usage, and access the developer API.',
    },
  },
  {
    pattern: '/privacy',
    meta: {
      title: `Privacy Policy · ${SITE_TAGLINE}`,
      description:
        'How GeoBorder collects, uses, and protects information about you when you use the public site or developer API.',
    },
  },
  {
    pattern: '/terms',
    meta: {
      title: `Terms of Use · ${SITE_TAGLINE}`,
      description:
        'Terms of use for the GeoBorder public site and developer API.',
    },
  },
  {
    pattern: '/security',
    meta: {
      title: `Security · ${SITE_TAGLINE}`,
      description:
        'GeoBorder security posture, vulnerability disclosure process, and account-security guidance.',
    },
  },
  {
    pattern: '/accessibility',
    meta: {
      title: `Accessibility · ${SITE_TAGLINE}`,
      description:
        'GeoBorder targets WCAG 2.1 AA / Section 508. How we test, what we support, and how to report a barrier.',
    },
  },
  {
    pattern: '/verify-email',
    meta: {
      title: `Verify Email · ${SITE_TAGLINE}`,
      description: 'Check your email to verify your GeoBorder account.',
      noindex: true,
    },
  },
  {
    pattern: '/forgot-password',
    meta: {
      title: `Reset Password · ${SITE_TAGLINE}`,
      description: 'Request a password reset for your GeoBorder account.',
      noindex: true,
    },
  },
]

// Authed / admin areas — title + description per surface, all noindex.
const AUTHED_META: Array<{ pattern: string; meta: RouteMeta }> = [
  { pattern: '/account', meta: { title: `Account · ${SITE_TAGLINE}`, description: 'GeoBorder account overview.', noindex: true } },
  { pattern: '/account/keys', meta: { title: `API Keys · ${SITE_TAGLINE}`, description: 'Manage your GeoBorder API keys.', noindex: true } },
  { pattern: '/account/playground', meta: { title: `Interactive Playground · ${SITE_TAGLINE}`, description: 'Try the GeoBorder API live from your browser.', noindex: true } },
  { pattern: '/account/reporting', meta: { title: `Reporting · ${SITE_TAGLINE}`, description: 'API usage analytics for your GeoBorder organization.', noindex: true } },
  { pattern: '/account/profile', meta: { title: `Profile · ${SITE_TAGLINE}`, description: 'GeoBorder account profile.', noindex: true } },
  { pattern: '/account/security', meta: { title: `Account Security · ${SITE_TAGLINE}`, description: 'GeoBorder account security settings.', noindex: true } },
  { pattern: '/account/org', meta: { title: `Organization · ${SITE_TAGLINE}`, description: 'GeoBorder organization management.', noindex: true } },
  { pattern: '/account/org/invite/:token', meta: { title: `Accept Invite · ${SITE_TAGLINE}`, description: 'Accept a GeoBorder organization invitation.', noindex: true } },
  { pattern: '/admin', meta: { title: `Admin · ${SITE_TAGLINE}`, description: 'GeoBorder admin console.', noindex: true } },
  { pattern: '/admin/users', meta: { title: `Admin · Users · ${SITE_TAGLINE}`, description: 'GeoBorder admin users.', noindex: true } },
  { pattern: '/admin/users/:userId', meta: { title: `Admin · User Detail · ${SITE_TAGLINE}`, description: 'GeoBorder admin user detail.', noindex: true } },
]

const FALLBACK: RouteMeta = {
  title: `Page Not Found · ${SITE_TAGLINE}`,
  description: 'The page you requested could not be found.',
  noindex: true,
}

function resolve(pathname: string): RouteMeta {
  for (const { pattern, meta } of ROUTE_META) {
    if (matchPath({ path: pattern, end: true }, pathname)) return meta
  }
  for (const { pattern, meta } of AUTHED_META) {
    if (matchPath({ path: pattern, end: true }, pathname)) return meta
  }
  return FALLBACK
}

export function RouteSeo() {
  const { pathname } = useLocation()
  const meta = resolve(pathname)
  return <SeoHead title={meta.title} description={meta.description} noindex={meta.noindex} />
}
